import { apiClient } from "@/lib/api";
import type { ErrorResponse, StandardResponse } from "@/types/api-response";
import { isErrorResponse } from "@/types/api-response";

/**
 * Result of a successful upload. Mirrors the backend `UploadFileResponse`.
 */
export interface UploadedFile {
  /** Attachment id to hand to whatever record the file belongs to. */
  attachmentId: string;
  /** API-relative path to the auth-protected file. Not usable directly in `<img src>`. */
  url: string;
  fileName: string;
  size: number;
  contentType: string;
}

/**
 * File uploads for the admin console — client for `POST /api/v1/uploads/file`.
 *
 * The endpoint keys the attachment off the current USER rather than a tenant, which is why the
 * admin API can reuse the same application-layer command the customer console uses. Content rules
 * (PNG/JPEG/PDF, max size, magic bytes) are enforced server-side; this only reports what came back.
 */
export const adminUploadService = {
  async uploadFile(file: File): Promise<UploadedFile> {
    const formData = new FormData();
    formData.append("file", file);

    const response = await apiClient.post<
      StandardResponse<UploadedFile> | ErrorResponse
    >("/api/v1/uploads/file", formData, {
      headers: { "Content-Type": "multipart/form-data" },
      timeout: 300_000,
    });

    if (isErrorResponse(response.data)) {
      throw new Error(response.data.error.message);
    }

    const result = response.data.data;

    // Fail loudly on a malformed envelope: without the attachment id the file is uploaded but
    // unreachable, and the UI would show it as attached while the record gets nothing.
    if (!result?.attachmentId) {
      throw new Error(
        "Upload succeeded but the server returned no attachment reference.",
      );
    }

    return result;
  },

  async removeUnusedUpload(attachmentId: string): Promise<void> {
    await apiClient.delete(`/api/v1/uploads/${attachmentId}`);
  },
};
